import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bot, Globe, FileText, Plus, Search, AlertTriangle } from "lucide-react";
import { Link } from "wouter";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { BotStatusToggle } from "@/components/bot-status-toggle";
import { PageShell } from "@/components/page-shell";
import { HelpAssistantPinnedCard } from "@/components/help-assistant-widget";
import type { Chatbot } from "@shared/schema";

function ChatbotTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "website":
      return <Globe className="w-4 h-4 text-white" />;
    case "document":
      return <FileText className="w-4 h-4 text-white" />;
    default:
      return <Bot className="w-4 h-4 text-white" />;
  }
}

export default function ChatbotsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: chatbots, isLoading } = useQuery<Chatbot[]>({
    queryKey: ["/api/chatbots"],
  });
  const { user, organization } = useAuth();
  // Same admin role list used elsewhere in the app (chatbot-detail.tsx).
  // Non-admins see the badge only — they can't toggle.
  const isAdminUser =
    !!user && ["admin", "super_admin", "org_admin", "it_admin", "hr_admin", "bot_admin"].includes(user.role || "");

  const filtered = chatbots?.filter((bot) =>
    bot.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Plan usage. We surface "X of Y enabled" so admins can see at a glance
  // how many enable slots they have left, and how many bots are sitting
  // disabled waiting for a slot.
  const totalCount = chatbots?.length ?? 0;
  const activeCount = chatbots?.filter((b) => (b.status ?? "active") === "active").length ?? 0;
  const botLimit = organization?.botLimit ?? null;
  const atLimit = !!botLimit && activeCount >= botLimit;

  const subtitle = (
    <>
      Manage all your AI chatbot configurations
      {botLimit != null && (
        <>
          {" · "}
          <span data-testid="text-bot-usage" className={atLimit ? "text-amber-600 font-medium" : ""}>
            {activeCount} of {botLimit} enabled
          </span>
          {totalCount > activeCount && (
            <span className="text-muted-foreground/80"> · {totalCount - activeCount} disabled</span>
          )}
        </>
      )}
    </>
  );

  return (
    <PageShell
      eyebrow={organization?.name}
      title="Chatbots"
      titleTestId="text-chatbots-title"
      subtitle={subtitle}
      actions={
        <Link href="/chatbots/new">
          <Button data-testid="button-new-chatbot">
            <Plus className="w-4 h-4 mr-2" />
            New Chatbot
          </Button>
        </Link>
      }
    >
      {atLimit && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/40 p-3"
          data-testid="banner-bot-limit"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-900 dark:text-amber-100">
              You've reached your plan's enabled-bot limit ({botLimit}).
            </p>
            <p className="text-amber-800/90 dark:text-amber-200/80 mt-0.5">
              Use the toggle on each bot to choose which ones run, or
              <Link href="/admin" className="underline ml-1" data-testid="link-upgrade-plan">upgrade your plan</Link>.
            </p>
          </div>
        </div>
      )}

      <HelpAssistantPinnedCard />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search chatbots..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="input-search-chatbots"
        />
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-10 w-10 rounded-md mb-3" />
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered && filtered.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((bot) => (
            <Link key={bot.id} href={`/chatbots/${bot.id}`}>
              <Card className="cursor-pointer hover-elevate transition-all h-full" data-testid={`card-chatbot-${bot.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: bot.primaryColor || "#0c3547" }}
                      >
                        <ChatbotTypeIcon type={bot.type} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm overflow-hidden text-ellipsis" data-testid={`text-bot-name-${bot.id}`}>{bot.name}</h3>
                        <p className="text-xs text-muted-foreground capitalize">{bot.type} chatbot</p>
                      </div>
                    </div>
                    {isAdminUser ? (
                      <BotStatusToggle bot={bot} />
                    ) : (
                      <Badge variant={bot.status === "active" ? "default" : "secondary"}>
                        {bot.status === "active" ? "Enabled" : "Disabled"}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                    {bot.systemPrompt || "No system prompt configured"}
                  </p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{bot.model}</Badge>
                    <Badge variant="outline" className="text-[10px]">Temp: {(bot.temperature ?? 7) / 10}</Badge>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <div className="dot-grid rounded-2xl border border-card-border px-6 py-14 sm:py-20 text-center">
          <div className="mx-auto w-20 h-20 rounded-full mb-6 shadow-lg flex items-center justify-center"
               style={{ background: "radial-gradient(circle at 30% 30%, hsl(150 70% 60%), hsl(155 75% 38%))" }}>
            <Bot className="w-9 h-9 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {searchQuery ? "No chatbots found" : "Welcome to your Chatbots"}
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
            {searchQuery
              ? "Try a different search term, or create a brand-new bot to get started."
              : "Create your first AI assistant. Choose a model, train it on your content, and embed it anywhere."}
          </p>

          {!searchQuery && (
            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto mt-8">
              <div className="feature-tile text-left">
                <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
                  <Bot className="w-4 h-4" />
                </div>
                <div className="font-medium text-sm">Productivity Boost</div>
                <p className="text-xs text-muted-foreground mt-1">Spin up an assistant trained on your docs in minutes.</p>
              </div>
              <div className="feature-tile text-left">
                <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(28 92% 48%), hsl(18 92% 42%))" }}>
                  <Globe className="w-4 h-4" />
                </div>
                <div className="font-medium text-sm">Multichannel Onboarding</div>
                <p className="text-xs text-muted-foreground mt-1">Web, Slack, Teams, SMS — one bot, every channel.</p>
              </div>
              <div className="feature-tile text-left">
                <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(340 80% 60%), hsl(320 75% 55%))" }}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="font-medium text-sm">Knowledge-Aware Replies</div>
                <p className="text-xs text-muted-foreground mt-1">Upload PDFs and URLs — answers cite the source.</p>
              </div>
            </div>
          )}

          <div className="mt-8">
            {!searchQuery && (
              <Link href="/chatbots/new">
                <Button size="lg" data-testid="button-create-first">
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first chatbot
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}
